/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView } from '@components/atoms'
import {
	mono,
	boFont,
	boShared,
	boMedia,
} from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogEntryType } from '@redux/types'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Button from '@mui/material/Button'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'

/** 폼 상태는 'YYYY-MM-DD' 문자열, DatePicker 만 Date 를 쓴다 (DESIGN_SYSTEM "날짜 입력") */
const toDate = (value?: string): Date | null =>
	value === undefined || value === '' ? null : dayjs(value).toDate()
const fromDate = (value: Date | null): string =>
	value === null || !dayjs(value).isValid() ? '' : dayjs(value).format('YYYY-MM-DD')

/** 상태코드 — 색이 아니라 칩 단계로: 2xx 희미, 4xx 외곽, 5xx 채움 */
const getStatusChipClass = (code: number | undefined): string => {
	if (!code) return 'faint'
	if (code >= 500) return 'solid'
	if (code >= 400) return 'outline'
	return 'faint'
}

/** 태그 — REQ 외곽, RES 희미, ERROR 채움 */
const getTagChipClass = (tag: string): string => {
	switch (tag) {
		case 'REQ':
			return 'outline'
		case 'ERROR':
			return 'solid'
		default:
			return 'faint'
	}
}

/**
 * 로그 상세 목록 — 필터 행 → 헤어라인 목록 → 페이지네이션. 상세·Trace 는 MUI Dialog(테마가 외형을 정한다).
 */
const LogListPage = () => {
	const dispatch = useAppDispatch()

	const listDate = useAppSelector(state => state.logDashboard.listDate)
	const listLevel = useAppSelector(state => state.logDashboard.listLevel)
	const listCountry = useAppSelector(state => state.logDashboard.listCountry)
	const listMethod = useAppSelector(state => state.logDashboard.listMethod)
	const listUrl = useAppSelector(state => state.logDashboard.listUrl)
	const listPage = useAppSelector(state => state.logDashboard.listPage)
	const listLimit = useAppSelector(state => state.logDashboard.listLimit)
	const logs = useAppSelector(state => state.logDashboard.logs)
	const logsTotalCount = useAppSelector(state => state.logDashboard.logsTotalCount)
	const logsStatus = useAppSelector(state => state.logDashboard.logsStatus)

	// trace
	const traces = useAppSelector(state => state.logDashboard.traces)
	const traceModalOpen = useAppSelector(state => state.logDashboard.traceModalOpen)
	const tracesStatus = useAppSelector(state => state.logDashboard.tracesStatus)

	// detail modal
	const detailModalOpen = useAppSelector(state => state.logDashboard.detailModalOpen)
	const detailModalLog = useAppSelector(state => state.logDashboard.detailModalLog)

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_LOG_LIST(true))
	}, [listDate, listPage, listLimit])

	const handleSearch = () => {
		dispatch(logDashboardActions.LIST_PAGE(1))
		dispatch(logDashboardActions.TRIGGER_GET_LOG_LIST(true))
	}

	const handleRowClick = (log: LogEntryType) => {
		dispatch(logDashboardActions.DETAIL_MODAL_LOG(log))
		dispatch(logDashboardActions.DETAIL_MODAL_OPEN(true))
	}

	const handleTraceClick = (e: React.MouseEvent, requestId: string) => {
		e.stopPropagation()
		if (!requestId) return
		dispatch(logDashboardActions.TRACE_REQUEST_ID(requestId))
		dispatch(logDashboardActions.TRIGGER_GET_TRACE(true))
	}

	const handleCloseDetailModal = () => {
		dispatch(logDashboardActions.DETAIL_MODAL_OPEN(false))
		dispatch(logDashboardActions.DETAIL_MODAL_LOG(null))
	}

	const handleCloseTraceModal = () => {
		dispatch(logDashboardActions.TRACE_MODAL_OPEN(false))
		dispatch(logDashboardActions.TRACES([]))
	}

	const handlePageChange = (_: unknown, newPage: number) => {
		dispatch(logDashboardActions.LIST_PAGE(newPage + 1))
	}

	const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(logDashboardActions.LIST_LIMIT(parseInt(e.target.value)))
		dispatch(logDashboardActions.LIST_PAGE(1))
	}

	const isLoading = logsStatus === 'loading'

	return (
		<View css={style}>
			<BoxView title="로그 상세 목록">
				<View className="listContainer">
					{/* Filters */}
					<View className="filterRow">
						<DatePicker
							label="날짜"
							value={toDate(listDate)}
							onChange={value => dispatch(logDashboardActions.LIST_DATE(fromDate(value)))}
							format="yyyy-MM-dd"
							slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
						/>
						<FormControl size="small" style={{ minWidth: 120 }}>
							<InputLabel>레벨</InputLabel>
							<Select
								value={listLevel}
								label="레벨"
								onChange={e => dispatch(logDashboardActions.LIST_LEVEL(e.target.value))}
							>
								<MenuItem value="">전체</MenuItem>
								<MenuItem value="INFO">INFO</MenuItem>
								<MenuItem value="ERROR">ERROR</MenuItem>
								<MenuItem value="DEBUG">DEBUG</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="국가"
							value={listCountry}
							onChange={e => dispatch(logDashboardActions.LIST_COUNTRY(e.target.value))}
							size="small"
							placeholder="예: KR"
						/>
						<FormControl size="small" style={{ minWidth: 120 }}>
							<InputLabel>Method</InputLabel>
							<Select
								value={listMethod}
								label="Method"
								onChange={e => dispatch(logDashboardActions.LIST_METHOD(e.target.value))}
							>
								<MenuItem value="">전체</MenuItem>
								<MenuItem value="GET">GET</MenuItem>
								<MenuItem value="POST">POST</MenuItem>
								<MenuItem value="PUT">PUT</MenuItem>
								<MenuItem value="DELETE">DELETE</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="URL 검색"
							value={listUrl}
							onChange={e => dispatch(logDashboardActions.LIST_URL(e.target.value))}
							size="small"
							placeholder="/api/..."
						/>
						<Button variant="contained" onClick={handleSearch}>
							검색
						</Button>
					</View>

					{isLoading ? (
						<View className="loadingView">
							<CircularProgress size={24} />
						</View>
					) : (
						<>
							<View className="list">
								<View className="listHead">
									<span>시간</span>
									<span>태그</span>
									<span>Method</span>
									<span>URL</span>
									<span>Status</span>
									<span className="right">응답시간</span>
									<span>국가</span>
									<span>사용자</span>
									<span>Request ID</span>
								</View>
								{(logs ?? []).map((log: LogEntryType, idx: number) => (
									<View
										key={`${log.requestId}-${log.tag}-${idx}`}
										className="listRow"
										role="button"
										tabIndex={0}
										onClick={() => handleRowClick(log)}
										onKeyDown={e => {
											if (e.key === 'Enter') handleRowClick(log)
										}}
									>
										<span className="nowrap num">{log.timestamp}</span>
										<span>
											<span className={`boChip ${getTagChipClass(log.tag)}`}>
												{log.tag}
											</span>
										</span>
										<span className="method">{log.method ?? '-'}</span>
										<span className="code">{log.url ?? '-'}</span>
										<span>
											{log.statusCode ? (
												<span className={`boChip ${getStatusChipClass(log.statusCode)}`}>
													{log.statusCode}
												</span>
											) : (
												'-'
											)}
										</span>
										<span className="right num">
											{log.responseTime != null ? `${log.responseTime}ms` : '-'}
										</span>
										<span>{log.country || '-'}</span>
										<span className="ellipsis dim">
											{log.userId || log.userEmail || '-'}
										</span>
										<span>
											{log.requestId ? (
												<button
													type="button"
													className="traceLink"
													onClick={e => handleTraceClick(e, log.requestId)}
												>
													{log.requestId.substring(0, 12) + '...'}
												</button>
											) : (
												'-'
											)}
										</span>
									</View>
								))}
								{(logs ?? []).length === 0 && <p className="boEmpty">데이터 없음</p>}
							</View>
							<TablePagination
								component="div"
								count={logsTotalCount}
								page={listPage - 1}
								onPageChange={handlePageChange}
								rowsPerPage={listLimit}
								onRowsPerPageChange={handleRowsPerPageChange}
								rowsPerPageOptions={[25, 50, 100]}
								labelRowsPerPage="페이지당 행"
							/>
						</>
					)}
				</View>
			</BoxView>

			{/* Detail Modal */}
			<Dialog
				open={detailModalOpen}
				onClose={handleCloseDetailModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					로그 상세
					<IconButton onClick={handleCloseDetailModal} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{detailModalLog && (
						<pre css={preStyle}>{JSON.stringify(detailModalLog, null, 2)}</pre>
					)}
				</DialogContent>
				<DialogActions>
					{detailModalLog?.requestId && (
						<Button
							variant="outlined"
							onClick={e => {
								handleCloseDetailModal()
								handleTraceClick(e as any, detailModalLog.requestId)
							}}
						>
							Trace 보기
						</Button>
					)}
					<Button onClick={handleCloseDetailModal}>닫기</Button>
				</DialogActions>
			</Dialog>

			{/* Trace Modal */}
			<Dialog
				open={traceModalOpen}
				onClose={handleCloseTraceModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					Request Trace
					<IconButton onClick={handleCloseTraceModal} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{tracesStatus === 'loading' ? (
						<View style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
							<CircularProgress size={24} />
						</View>
					) : (traces ?? []).length === 0 ? (
						<p css={{ ...boShared['.boEmpty'] }}>Trace 데이터 없음</p>
					) : (
						<View css={traceStyle} className="traceTimeline">
							{(traces ?? []).map((trace: LogEntryType, idx: number) => (
								<View key={`${trace.tag}-${idx}`} className="traceItem">
									<View className="traceHead">
										<span className={`boChip ${getTagChipClass(trace.tag)}`}>
											{trace.tag}
										</span>
										<span className="traceTime">{trace.timestamp}</span>
									</View>
									{trace.tag === 'REQ' && (
										<>
											<p className="traceCode">{`${trace.method} ${trace.url}`}</p>
											<p className="traceMeta">
												{`${trace.clientIP} / ${trace.country} ${trace.city}`}
											</p>
										</>
									)}
									{trace.tag === 'RES' && (
										<p className="traceText">
											{`Status: ${trace.statusCode} | ${trace.responseTime}ms`}
										</p>
									)}
									{trace.tag === 'ERROR' && (
										<p className="traceText strong">
											{`${trace.error?.code}: ${trace.error?.message}`}
										</p>
									)}
								</View>
							))}
						</View>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseTraceModal}>닫기</Button>
				</DialogActions>
			</Dialog>
		</View>
	)
}

export default LogListPage

const preStyle = css({
	margin: 0,
	padding: 16,
	background: mono.bg,
	border: `1px solid ${mono.line}`,
	color: mono.ink,
	fontFamily: boFont.mono,
	fontSize: 12,
	lineHeight: 1.6,
	overflow: 'auto',
	maxHeight: '60vh',
})

const traceStyle = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',
	borderTop: `1px solid ${mono.line}`,

	'.traceItem': {
		padding: '14px 0',
		borderBottom: `1px solid ${mono.line}`,

		'.traceHead': { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
		'.traceTime': {
			fontSize: 11,
			letterSpacing: '.08em',
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
		'.traceCode': { fontFamily: boFont.mono, fontSize: 12, color: mono.ink },
		'.traceMeta': { marginTop: 4, fontSize: 12, fontWeight: 300, color: mono.dim },
		'.traceText': { fontSize: 13, fontWeight: 300, color: mono.ink },
		'.strong': { fontWeight: 500 },
	},
})

const style = css({
	...boShared,
	height: '100%',

	'.listContainer': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
	},

	'.filterRow': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		flexWrap: 'wrap',
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '20rem',
	},

	'.list': {
		borderTop: `1px solid ${mono.line}`,
		overflowX: 'auto',

		'.listHead, .listRow': {
			display: 'grid',
			gridTemplateColumns:
				'150px 64px 64px minmax(160px, 1.6fr) 64px 80px 56px minmax(100px, 1fr) 130px',
			alignItems: 'center',
			gap: 12,
			padding: '9px 0',
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 13,
			fontWeight: 300,
			color: mono.ink,
			minWidth: 960,
		},

		'.listHead': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
		},

		'.listRow': {
			cursor: 'pointer',
			transition: 'background-color 200ms ease-out',
			'&:hover': { background: mono.surface },
			'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: -1 },
		},

		'.right': { textAlign: 'right' },
		'.num': { fontVariantNumeric: 'tabular-nums' },
		'.nowrap': { whiteSpace: 'nowrap' },
		'.dim': { color: mono.dim },
		'.method': { fontWeight: 500, fontSize: 12, letterSpacing: '.04em' },
		'.code': {
			fontFamily: boFont.mono,
			fontSize: 12,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
		'.ellipsis': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

		'.traceLink': {
			padding: 0,
			paddingBottom: 1,
			border: 0,
			borderBottom: `1px solid ${mono.lineSoft}`,
			background: 'transparent',
			fontFamily: boFont.mono,
			fontSize: 11,
			color: mono.dim,
			cursor: 'pointer',
			transition: 'color 200ms ease-out, border-color 200ms ease-out',
			'&:hover': { color: mono.ink, borderBottomColor: mono.ink },
		},
	},
	// 폰 — 9열 표를 카드로 (2026-09-29). 가로 스크롤을 없앤다
	[boMedia.phone]: {
		'.list': { overflowX: 'visible', borderTop: 0 },
		'.listHead': { display: 'none' },
		'.listRow': {
			display: 'flex',
			flexWrap: 'wrap',
			alignItems: 'center',
			gap: '4px 10px',
			minWidth: 0,
			padding: '12px 14px',
			marginBottom: 8,
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			fontSize: 13,
			// URL 과 Request ID 는 제 줄을 갖는다
			'& > span:nth-of-type(4)': {
				order: 10,
				flexBasis: '100%',
				minWidth: 0,
				overflowWrap: 'anywhere',
				whiteSpace: 'normal',
			},
			'& > span:nth-of-type(9)': {
				order: 20,
				flexBasis: '100%',
				minWidth: 0,
				fontSize: 11,
				color: mono.faint,
				overflowWrap: 'anywhere',
			},
			'& > span.right': { textAlign: 'left' },
		},
	},
})
